import type { DocPerm, DocPlan, MapNode, NodeId, NodeMap } from './types';

/**
 * The clearance heatmap's LENS (V0.39) — pure, DOM-free, and deliberately date-free.
 *
 * ONE lens: per node, what was PLANNED (`Node.expectedDocs`, already on `/api/map`) against
 * what is ATTACHED (the four slots plus `customDocs`, same payload) against what is CLEARED
 * (`GET /api/map/health` — per-node workflow counts for the current locale, computed by the
 * server's one aggregation). FIVE buckets, each with a colour AND an icon (V0.42), and — the
 * root excepted — NO NODE KEEPS ITS OWN COLOUR:
 *
 *   blue   UNPLANNED — no plan at all. "Nobody has said what this node owes." NO PLAN WINS:
 *          blue whatever is attached, decided over blue-only-when-also-empty. Icon ❓.
 *   red    MISSING — the plan names documentation that is not attached, a broken promise.
 *          Only a planned node reaches here. Icon ⚠.
 *   slate  EXEMPT — the plan is `{}`, "nothing expected here", stated. The one bucket with NO
 *          icon and a muted colour: an exemption needs no alarm.
 *   amber  UNCLEARED — everything expected is attached and the node owns documents, but not
 *          every owned page is `approved` in the current locale (a page with no row in this
 *          locale counts as uncleared). Icon ❗.
 *   green  CLEARED — everything expected is attached and EVERY owned page is approved. Icon ✓.
 *   (null) the ROOT only — it is the map's title, not a documentation subject, and keeps its
 *          gradient. Every other node takes a bucket.
 *
 * NO `Date.now()` ANYWHERE, on purpose. Clearance has no dates, and a bucketer that read the
 * clock would produce a different `heatTitle` every publish — `useShallow` would stop
 * short-circuiting and every publish would re-render 147 nodes. Everything here is a pure
 * function of the map payload and the health payload, so the strings are stable until the
 * data actually moves.
 *
 * `HEAT_HEX` is a DEDICATED bright palette (V0.42), NOT the app's status colours — see its
 * own note. Plain hexes rather than custom properties because the paint path (`styleFor` in
 * MapNode.tsx) feeds them through `hexA()`/`shade()`, string maths over `#rrggbb` that a
 * `var(...)` could not compute.
 */

/**
 * The plan says "nothing expected here" — `expectedDocs = {}`. The successor to the retired
 * "No doc" tag (V0.42), and the ONE place that predicate lives: an exempt node is excluded
 * from coverage stats and the AI pipeline, and reads "nothing expected" rather than "missing".
 * `null`/`undefined` (no plan at all) is NOT exempt — it is unplanned, a different state.
 */
export function isExemptPlan(plan: DocPlan | null | undefined): boolean {
  return plan != null && Object.keys(plan).length === 0;
}

export type HeatBucket = 'red' | 'amber' | 'green' | 'slate' | 'blue';

/**
 * The heatmap's DEDICATED bright palette (V0.42) — deliberately NOT the app's muted status
 * colours, so a node in the heatmap can never be mistaken for a node someone coloured by hand.
 * These are the one definition (there is no `--heat-*` CSS variable — the paint path in
 * MapNode.tsx feeds these through `hexA()`/`shade()`, string maths over `#rrggbb`, which a
 * `var(...)` could not compute). Each bucket also carries an ICON (`heatIcon` below), because
 * colour alone fails a projector, colour-blindness, and a node already coloured green by hand.
 */
export const HEAT_HEX: Record<HeatBucket, string> = {
  green: '#12c04a', // CLEARED — bright green, everything planned attached and approved
  amber: '#ffcc00', // UNCLEARED — bright yellow, attached but not all approved
  red: '#ff2b2b', // MISSING — bright red, the plan names a document that is not there
  slate: '#94a3b8', // EXEMPT — desaturated grey, "nothing expected", the one bucket with no icon
  blue: '#38bdf8', // UNPLANNED — light blue, no plan at all: nobody has said what this owes
};

/** The `<symbol>` id each bucket stamps on its node. Slate is the deliberate blank. */
export const HEAT_ICON: Record<HeatBucket, string> = {
  green: 'i-check', // ✓ (already in index.html)
  amber: 'i-exclaim', // ❗
  red: 'i-warn', // ⚠
  slate: '', // none, deliberately — an exemption needs no alarm
  blue: 'i-question', // ❓
};

/** One node's workflow counts, as `GET /api/map/health` ships them (integers only). */
export interface NodeHealthCounts {
  /** Doc pages attached to this node (the four slots plus the custom list). */
  docs: number;
  approved: number;
  inReview: number;
  generated: number;
  needsUpdate: number;
  draft: number;
  /** Attached pages with no translation row in this locale at all. Uncleared, honestly. */
  missing: number;
}

export interface HeatEntry {
  heat: HeatBucket;
  /** The `<symbol>` id for the node badge — `HEAT_ICON[heat]`; '' for slate (no badge). */
  icon: string;
  /** The tooltip sentence. Stable per data — never per render, never per clock tick. */
  title: string;
}

/** What one publish builds ONCE (`mapContextOf`'s WeakMap) and every node reads from. */
export interface HeatIndex {
  /** False when the mode is off or the health payload has not (successfully) loaded. */
  active: boolean;
  byNode: ReadonlyMap<NodeId, HeatEntry>;
  /** Live tallies for the legend. Zeroes while inactive. */
  counts: Record<HeatBucket, number>;
}

const ZERO_COUNTS: Record<HeatBucket, number> = { red: 0, amber: 0, green: 0, slate: 0, blue: 0 };

export const EMPTY_HEAT_INDEX: HeatIndex = Object.freeze({
  active: false,
  byNode: new Map<NodeId, HeatEntry>(),
  counts: Object.freeze({ ...ZERO_COUNTS }) as Record<HeatBucket, number>,
});

/** The four planned kinds, each with the node field that says "attached" and a label for
 *  the red bucket's tooltip. `custom` is handled apart — it is a count, not a flag. */
const PLAN_SLOTS: readonly {
  kind: keyof Pick<DocPlan, 'documentation' | 'internal' | 'course' | 'demo'>;
  field: 'docId' | 'internalDocId' | 'courseDocId' | 'demoDocId';
  label: string;
}[] = [
  { kind: 'documentation', field: 'docId', label: 'documentation' },
  { kind: 'internal', field: 'internalDocId', label: 'internal documentation' },
  { kind: 'course', field: 'courseDocId', label: 'course' },
  { kind: 'demo', field: 'demoDocId', label: 'demo' },
];

/** The slice of a map node the lens reads. `MapNode` satisfies it structurally. */
export type HeatNodeFacts = Pick<
  MapNode,
  'root' | 'expectedDocs' | 'docId' | 'internalDocId' | 'courseDocId' | 'demoDocId' | 'customDocs'
>;

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** What one node's plan OWES but has not attached (V0.42) — the dotted "planned" pills. The
 *  ONE bridge from plan keys to attachment, shared by the three row-counting sites so they
 *  cannot drift (nodeView's `beanRowsOf` and `expandedRows`, and `docExtraHeight`). Gated by
 *  `canDocBtn`: a kind this viewer may not see is neither drawn nor counted. */
export interface PlannedMissing {
  /** Fixed kinds (as `DocPerm`) the plan names and the node has not attached. */
  kinds: ('documentation' | 'internal' | 'course' | 'demo')[];
  /** Custom documents owed beyond those attached. */
  customMissing: number;
}
export function plannedMissing(
  n: HeatNodeFacts,
  canDocBtn: (perm: DocPerm) => boolean,
): PlannedMissing {
  const plan = n.expectedDocs ?? null;
  if (!plan) return { kinds: [], customMissing: 0 };
  const kinds = PLAN_SLOTS.filter((s) => plan[s.kind] && !n[s.field] && canDocBtn(s.kind)).map((s) => s.kind);
  const owed = typeof plan.custom === 'number' ? plan.custom : 0;
  const attached = n.customDocs?.length ?? 0;
  const customMissing = canDocBtn('custom') ? Math.max(0, owed - attached) : 0;
  return { kinds, customMissing };
}

/**
 * WHAT A DOTTED "THIS IS OWED" PILL SAYS, on every surface that draws one (V0.43.2).
 *
 * The map's rows, the node panel and the band above a document all draw the same pill for the
 * same fact, and all three used to label it differently — the kind's bare name here, a neutral
 * "+ Kind" create pill there. One helper so a fourth surface cannot invent a fifth wording,
 * and the leading "+" so the pill reads as the ACTION it is rather than as a document that
 * exists.
 */
export const OWED_PILL_LABEL = (kindLabel: string): string => `+ ${kindLabel}`;
/** The name an owed CUSTOM document goes by — it has no kind of its own to be named after. */
export const OWED_CUSTOM_LABEL = 'Custom document';

/** Total planned-missing ROWS a node contributes — the count all three layout sites add. */
export function plannedMissingCount(n: HeatNodeFacts, canDocBtn: (perm: DocPerm) => boolean): number {
  const pm = plannedMissing(n, canDocBtn);
  return pm.kinds.length + pm.customMissing;
}

/** Build an entry, filling `icon` from the bucket so the two never drift. */
const entry = (heat: HeatBucket, title: string): HeatEntry => ({ heat, icon: HEAT_ICON[heat], title });

/**
 * One node's bucket. Every non-root node gets one now (V0.42) — "no node keeps its own
 * colour" — so this returns null ONLY for the root, which keeps its gradient.
 *
 * Precedence, and it matters: NO PLAN WINS. An unplanned node is BLUE whatever it owns
 * ("nobody has said what this node owes"), decided over the alternative of blue-only-when-
 * also-empty. Then, for a planned node: red (a named document is missing) → slate (exempt,
 * {}) → the clearance split (green all approved / amber not yet).
 *
 * `health` may be undefined for a node that owns documents — the payload is sparse and a page
 * can be attached between fetches. That reads as ALL UNCLEARED (`missing` = everything),
 * honest and conservative in the right direction.
 */
export function heatOf(n: HeatNodeFacts, health: NodeHealthCounts | undefined): HeatEntry | null {
  if (n.root) return null;

  const plan = n.expectedDocs ?? null;

  // UNPLANNED wins. No plan at all → blue, whatever is attached: a to-do that says "declare
  // what this node owes", not an absence of one.
  if (plan === null) {
    return entry('blue', 'No documentation plan yet — declare what this node owes');
  }

  const customAttached = n.customDocs?.length ?? 0;
  const slotsAttached = PLAN_SLOTS.reduce((sum, s) => sum + (n[s.field] ? 1 : 0), 0);
  const ownedDocs = slotsAttached + customAttached;

  // RED: a kind the plan names is not attached. Before the {} case: an empty plan names
  // nothing, so it can never be red.
  const missing: string[] = [];
  for (const s of PLAN_SLOTS) if (plan[s.kind] && !n[s.field]) missing.push(s.label);
  const owedCustom = typeof plan.custom === 'number' ? plan.custom : 0;
  if (owedCustom > customAttached) {
    missing.push(`${owedCustom - customAttached} of ${plural(owedCustom, 'custom document')}`);
  }
  if (missing.length) {
    return entry('red', `Planned but not attached: ${missing.join(', ')}`);
  }

  // SLATE: `{}` — "nothing expected here", stated. An explicit exemption, whatever is attached.
  if (Object.keys(plan).length === 0) {
    return entry('slate', 'Nothing expected here — stated in the plan');
  }

  // The plan is met (so at least one document is attached): the clearance split.
  const h: NodeHealthCounts = health ?? {
    docs: ownedDocs,
    approved: 0,
    inReview: 0,
    generated: 0,
    needsUpdate: 0,
    draft: 0,
    missing: ownedDocs,
  };
  const docs = h.docs > 0 ? h.docs : Math.max(1, ownedDocs);
  if (h.approved >= docs) {
    return entry('green', `All ${plural(docs, 'document')} approved in this language`);
  }
  return entry('amber', `${h.approved} of ${plural(docs, 'document')} approved in this language`);
}

export interface HeatIndexInput {
  /** The mode (`S.heatmapOn`). Off = the empty index, and every node keeps its colour. */
  on: boolean;
  /** Whether `GET /api/map/health` has successfully answered. A failed fetch leaves this
   *  false, and the mode stays on over an EMPTY index — colours rather than guesses. */
  loaded: boolean;
  /** The health payload, sparse by construction. */
  byNode: Record<NodeId, NodeHealthCounts>;
  nodes: NodeMap;
  /** Permission-hidden nodes take no heat and are not counted — the legend must not tally
   *  branches this user cannot see. */
  hidden?: (id: NodeId) => boolean;
}

/** Build the whole map's heat ONCE per publish. Never call this per node. */
export function buildHeatIndex(input: HeatIndexInput): HeatIndex {
  if (!input.on || !input.loaded) return EMPTY_HEAT_INDEX;
  const byNode = new Map<NodeId, HeatEntry>();
  const counts: Record<HeatBucket, number> = { ...ZERO_COUNTS };
  for (const id in input.nodes) {
    if (input.hidden?.(id)) continue;
    const entry = heatOf(input.nodes[id], input.byNode[id]);
    if (!entry) continue;
    byNode.set(id, entry);
    counts[entry.heat]++;
  }
  return { active: true, byNode, counts };
}
